import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  SkipForward,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/features/auth';
import { type Language } from '@/types';
import {
  type CleanerJobBlock,
  type CleanerVisitJob,
  loadCleanerJobs,
  updateCleanerJobBlockStatus,
} from '../api';

interface CleanerWorkspaceProps {
  language: Language;
}

type NoticeState = {
  tone: 'success' | 'error';
  text: string;
};

function todayKey() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function visitProgress(visit: CleanerVisitJob) {
  const actionable = visit.blocks.filter((block) => block.status !== 'cancelled').length;
  const done = visit.blocks.filter((block) => block.status === 'completed' || block.status === 'skipped').length;
  return { actionable, done };
}

function groupByDate(visits: CleanerVisitJob[]) {
  const grouped = new Map<string, CleanerVisitJob[]>();
  for (const visit of visits) {
    const current = grouped.get(visit.scheduledFor) ?? [];
    current.push(visit);
    grouped.set(visit.scheduledFor, current);
  }
  return [...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function InlineNotice({ notice }: { notice: NoticeState | null }) {
  if (!notice) return null;
  return (
    <div
      className={
        notice.tone === 'success'
          ? 'rounded border border-[#1B7F4D]/30 bg-[#1B7F4D]/[0.10] px-4 py-3 text-sm font-medium text-[#1B7F4D]'
          : 'rounded border border-[#C24138]/30 bg-[#C24138]/[0.12] px-4 py-3 text-sm font-medium text-[#C24138]'
      }
    >
      {notice.text}
    </div>
  );
}

// Status → 2px left rail color (the mobile signature, replaces reticle ticks at card size)
function statusRail(status: CleanerVisitJob['status']): string {
  if (status === 'completed' || status === 'approved') return '#1B7F4D';
  return '#B97E1F';
}

function blockRail(status: CleanerJobBlock['status']): string {
  if (status === 'completed') return '#1B7F4D';
  if (status === 'skipped') return '#B97E1F';
  if (status === 'cancelled') return '#C24138';
  return 'rgba(7,51,49,0.12)';
}

type BlockCollapsibleProps = {
  block: CleanerJobBlock;
  language: Language;
  activeBlockId: string | null;
  blockNotes: Record<string, string>;
  onSubmitStatus: (blockId: string, status: CleanerJobBlock['status']) => void;
  onNotesChange: (blockId: string, value: string) => void;
};

const BlockCollapsible: React.FC<BlockCollapsibleProps> = ({
  block,
  language,
  activeBlockId,
  blockNotes,
  onSubmitStatus,
  onNotesChange,
}) => {
  const isTerminal = block.status !== 'planned';
  const [isOpen, setIsOpen] = useState(!isTerminal);
  const isBusy = activeBlockId === block.id;

  return (
    <div
      className="cmd-panel rounded overflow-hidden border-l-2"
      style={{ borderLeftColor: blockRail(block.status) }}
    >
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-4 text-left min-h-[56px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {block.status === 'completed' ? (
          <CheckCircle2 className="h-5 w-5 shrink-0 text-[#1B7F4D]" />
        ) : block.status === 'skipped' ? (
          <SkipForward className="h-5 w-5 shrink-0 text-[#B97E1F]" />
        ) : block.status === 'cancelled' ? (
          <XCircle className="h-5 w-5 shrink-0 text-[#C24138]" />
        ) : isOpen ? (
          <ChevronDown className="h-5 w-5 shrink-0 text-[#607C6E]" />
        ) : (
          <ChevronRight className="h-5 w-5 shrink-0 text-[#607C6E]" />
        )}
        <div className="flex-1 space-y-0.5">
          <div className={`font-medium text-[#073331] ${block.status === 'cancelled' ? 'line-through text-[#607C6E]' : ''}`}>
            {block.area}
          </div>
          <div className={`text-sm ${block.status === 'cancelled' ? 'line-through text-[#607C6E]' : 'text-[#2E4640]'}`}>
            {block.label}
          </div>
        </div>
        {block.requiredPhotos > 0 && (
          <span className="shrink-0 rounded-full border border-[#8A6A47]/40 px-2.5 py-1 font-mono text-xs tabular-nums text-[#8A6A47]">
            {block.requiredPhotos} {language === 'sv' ? 'foto' : 'photos'}
          </span>
        )}
      </button>

      {isOpen && block.status === 'planned' && (
        <div className="space-y-3 border-t border-[#073331]/10 px-4 pb-4 pt-4">
          <Textarea
            value={blockNotes[block.id] ?? block.completionNotes ?? ''}
            onChange={(event) => onNotesChange(block.id, event.target.value)}
            placeholder={language === 'sv' ? 'Kommentar eller avvikelse…' : 'Add a note or deviation…'}
            rows={3}
            className="border-[#073331]/15 bg-white text-base text-[#2E4640] placeholder:text-[#607C6E] focus-visible:ring-[#073331]/20"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onSubmitStatus(block.id, 'completed')}
              disabled={isBusy}
              className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded bg-[#073331] px-4 text-sm font-medium text-[#F0EEE6] transition-colors hover:bg-[#0B423E] disabled:opacity-50"
            >
              {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {language === 'sv' ? 'Klarmarkera' : 'Complete'}
            </button>
            <button
              type="button"
              onClick={() => onSubmitStatus(block.id, 'skipped')}
              disabled={isBusy}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded border border-[#8A6A47]/50 px-4 text-sm font-medium text-[#8A6A47] transition-colors hover:border-[#6E5438] hover:text-[#6E5438] disabled:opacity-50"
            >
              <SkipForward className="h-4 w-4" />
              {language === 'sv' ? 'Hoppa över' : 'Skip'}
            </button>
            <button
              type="button"
              onClick={() => onSubmitStatus(block.id, 'cancelled')}
              disabled={isBusy}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded border border-[#073331]/15 px-4 text-sm font-medium text-[#607C6E] transition-colors hover:border-[#C24138]/50 hover:text-[#C24138] disabled:opacity-50"
            >
              <XCircle className="h-4 w-4" />
              {language === 'sv' ? 'Avbryt' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

type VisitCardProps = {
  visit: CleanerVisitJob;
  language: Language;
  onClick: () => void;
};

const VisitCard: React.FC<VisitCardProps> = ({
  visit,
  language,
  onClick,
}) => {
  const progress = visitProgress(visit);
  const isCompleted = visit.status === 'completed' || visit.status === 'approved';
  const isApproved = visit.status === 'approved';

  return (
    <button
      type="button"
      className="cmd-panel w-full rounded border-l-2 px-4 py-4 text-left transition-colors hover:bg-[#073331]/[0.03]"
      style={{ borderLeftColor: statusRail(visit.status) }}
      onClick={onClick}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="font-medium text-[#073331]">{visit.siteName}</div>
          <div className="font-mono text-sm tabular-nums text-[#607C6E]">{visit.scheduledFor}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isCompleted && <CheckCircle2 className="h-4 w-4 text-[#1B7F4D]" />}
          <span
            className={
              isApproved
                ? 'rounded-full border border-[#1B7F4D]/30 bg-[#1B7F4D]/[0.10] px-2.5 py-1 text-xs font-semibold text-[#1B7F4D]'
                : visit.status === 'completed'
                  ? 'rounded-full border border-[#B97E1F]/30 bg-[#B97E1F]/[0.12] px-2.5 py-1 text-xs font-semibold text-[#B97E1F]'
                  : 'rounded-full border border-[#073331]/12 bg-[#073331]/[0.04] px-2.5 py-1 text-xs font-semibold text-[#607C6E]'
            }
          >
            {visit.status === 'completed'
              ? (language === 'sv' ? 'Väntar godkännande' : 'Pending approval')
              : isApproved
                ? (language === 'sv' ? 'Godkänd' : 'Approved')
                : (language === 'sv' ? 'Tilldelat' : 'Assigned')}
          </span>
          <span className="rounded-full border border-[#073331]/12 px-2.5 py-1 font-mono text-xs tabular-nums text-[#2E4640]">
            {progress.done}/{progress.actionable}
          </span>
        </div>
      </div>
    </button>
  );
}

export function CleanerWorkspace({ language }: CleanerWorkspaceProps) {
  const { session, profile } = useAuth();
  const [jobs, setJobs] = useState<CleanerVisitJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
  const [blockNotes, setBlockNotes] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const sessionToken = session?.access_token ?? null;

  const refreshJobs = async (mode: 'load' | 'refresh' = 'refresh') => {
    if (mode === 'refresh') {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      const nextJobs = await loadCleanerJobs(sessionToken);
      setJobs(nextJobs);
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Could not load assigned jobs.',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    void refreshJobs('load');
  }, [sessionToken]);

  const today = todayKey();
  const todayJobs = useMemo(
    () => jobs.filter((job) => job.scheduledFor === today && job.status === 'assigned'),
    [jobs, today],
  );
  const upcomingJobs = useMemo(
    () => jobs.filter((job) => job.scheduledFor > today && job.status === 'assigned'),
    [jobs, today],
  );
  const completedJobs = useMemo(
    () => jobs.filter((job) => job.status === 'completed' || job.status === 'approved'),
    [jobs],
  );
  const selectedVisit = jobs.find((job) => job.id === selectedVisitId) ?? null;

  const submitBlockStatus = async (blockId: string, status: CleanerJobBlock['status']) => {
    setActiveBlockId(blockId);
    setNotice(null);

    try {
      await updateCleanerJobBlockStatus(
        {
          jobBlockId: blockId,
          status,
          completionNotes: blockNotes[blockId] ?? '',
        },
        sessionToken,
      );
      await refreshJobs();
      setNotice({
        tone: 'success',
        text: status === 'completed'
          ? (language === 'sv' ? 'Block markerat som klart.' : 'Block marked as complete.')
          : status === 'skipped'
            ? (language === 'sv' ? 'Block hoppades över.' : 'Block skipped.')
            : (language === 'sv' ? 'Block avbrutet.' : 'Block cancelled.'),
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Could not update block status.',
      });
    } finally {
      setActiveBlockId(null);
    }
  };

  const handleNotesChange = (blockId: string, value: string) => {
    setBlockNotes((current) => ({ ...current, [blockId]: value }));
  };

  if (selectedVisit) {
    const detailApproved = selectedVisit.status === 'approved';
    const detailCompleted = selectedVisit.status === 'completed';
    return (
      <div className="cmd-canvas fixed inset-0 z-50 flex flex-col">
        <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-[#073331]/10 bg-white px-4 py-3">
          <Button type="button" variant="ghost" size="icon" className="text-[#073331] hover:bg-[#073331]/[0.06]" onClick={() => setSelectedVisitId(null)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 space-y-0.5">
            <div className="font-semibold leading-tight text-[#073331]">{selectedVisit.siteName}</div>
            <div className="font-mono text-xs tabular-nums text-[#607C6E]">
              {selectedVisit.scheduledFor} · {selectedVisit.totalPoints.toFixed(2)} {language === 'sv' ? 'poäng' : 'pts'}
            </div>
          </div>
          <span
            className={
              detailApproved
                ? 'rounded-full border border-[#1B7F4D]/30 bg-[#1B7F4D]/[0.10] px-2.5 py-1 text-xs font-semibold text-[#1B7F4D]'
                : detailCompleted
                  ? 'rounded-full border border-[#B97E1F]/30 bg-[#B97E1F]/[0.12] px-2.5 py-1 text-xs font-semibold text-[#B97E1F]'
                  : 'rounded-full border border-[#073331]/12 bg-[#073331]/[0.04] px-2.5 py-1 text-xs font-semibold text-[#607C6E]'
            }
          >
            {detailCompleted
              ? (language === 'sv' ? 'Väntar godkännande' : 'Pending')
              : detailApproved
                ? (language === 'sv' ? 'Godkänd' : 'Approved')
                : (language === 'sv' ? 'Tilldelat' : 'Assigned')}
          </span>
        </header>

        <div className="px-4 pt-3">
          <InlineNotice notice={notice} />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {selectedVisit.blocks.map((block) => (
            <BlockCollapsible
              key={block.id}
              block={block}
              language={language}
              activeBlockId={activeBlockId}
              blockNotes={blockNotes}
              onSubmitStatus={submitBlockStatus}
              onNotesChange={handleNotesChange}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-[#073331]">{language === 'sv' ? 'Mina jobb' : 'My jobs'}</h1>
        <p className="text-sm text-[#607C6E]">
          {language === 'sv'
            ? 'Dina tilldelade jobb. Öppna ett jobb för att markera block som klara.'
            : 'Your assigned jobs. Open a job to update block statuses.'}
        </p>
      </header>

      <div className="cmd-panel flex items-center justify-between gap-3 rounded px-4 py-3">
        <div>
          <div className="font-medium text-[#073331]">{profile?.full_name ?? profile?.email ?? 'Cleaner'}</div>
          <div className="cmd-label">
            {language === 'sv' ? 'Roll · Städare' : 'Role · Cleaner'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void refreshJobs()}
          disabled={isRefreshing || isLoading}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded border border-[#8A6A47]/50 px-4 text-sm font-medium text-[#8A6A47] transition-colors hover:border-[#6E5438] hover:text-[#6E5438] disabled:opacity-50"
        >
          {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {language === 'sv' ? 'Uppdatera' : 'Refresh'}
        </button>
      </div>

      <InlineNotice notice={notice} />

      {isLoading ? (
        <div className="cmd-panel rounded p-6 text-sm text-[#607C6E]">
          {language === 'sv' ? 'Laddar tilldelade jobb…' : 'Loading assigned jobs…'}
        </div>
      ) : (
        <Tabs defaultValue="today">
          <TabsList className="w-full">
            <TabsTrigger value="today" className="flex-1">
              {language === 'sv' ? 'Idag' : 'Today'}
              {todayJobs.length > 0 && <span className="ml-1.5 font-mono text-xs tabular-nums text-[#607C6E]">({todayJobs.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="flex-1">
              {language === 'sv' ? 'Kommande' : 'Upcoming'}
              {upcomingJobs.length > 0 && <span className="ml-1.5 font-mono text-xs tabular-nums text-[#607C6E]">({upcomingJobs.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex-1">
              {language === 'sv' ? 'Jobb' : 'Jobs'}
              {completedJobs.length > 0 && <span className="ml-1.5 font-mono text-xs tabular-nums text-[#607C6E]">({completedJobs.length})</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="space-y-3 pt-3">
            {todayJobs.length === 0 ? (
              <div className="cmd-panel rounded p-4 text-sm text-[#607C6E]">
                {language === 'sv' ? 'Inga jobb tilldelade idag.' : 'No jobs assigned today.'}
              </div>
            ) : (
              todayJobs.map((visit) => (
                <VisitCard key={visit.id} visit={visit} language={language} onClick={() => setSelectedVisitId(visit.id)} />
              ))
            )}
          </TabsContent>

          <TabsContent value="upcoming" className="space-y-5 pt-3">
            {upcomingJobs.length === 0 ? (
              <div className="cmd-panel rounded p-4 text-sm text-[#607C6E]">
                {language === 'sv' ? 'Inga kommande jobb just nu.' : 'No upcoming jobs right now.'}
              </div>
            ) : (
              groupByDate(upcomingJobs).map(([date, visits]) => (
                <div key={date} className="space-y-2">
                  <div className="cmd-label font-mono tabular-nums" style={{ fontSize: '11px' }}>{date}</div>
                  <div className="space-y-3">
                    {visits.map((visit) => (
                      <VisitCard key={visit.id} visit={visit} language={language} onClick={() => setSelectedVisitId(visit.id)} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="jobs" className="space-y-3 pt-3">
            {completedJobs.length === 0 ? (
              <div className="cmd-panel rounded p-4 text-sm text-[#607C6E]">
                {language === 'sv' ? 'Inga avslutade jobb ännu.' : 'No completed jobs yet.'}
              </div>
            ) : (
              completedJobs.map((visit) => (
                <VisitCard key={visit.id} visit={visit} language={language} onClick={() => setSelectedVisitId(visit.id)} />
              ))
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}